const TOKEN_STORAGE_KEY = "hillory.auth";
const API_ORIGIN = (import.meta.env?.VITE_API_ORIGIN ?? "").replace(/\/$/, "");
const REQUIRES_ORIGIN = import.meta.env?.PROD === true;

if (REQUIRES_ORIGIN && API_ORIGIN === "") {
  throw new Error(
    "VITE_API_ORIGIN is not set. A production build must be given the API origin at build time.",
  );
}

export function apiUrl(path: string): string {
  return `${API_ORIGIN}${path}`;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, "SESSION_EXPIRED", "Your session has expired. Please sign in again.");
    this.name = "SessionExpiredError";
  }
}

function readTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthTokens>;
    if (!parsed.accessToken || !parsed.refreshToken) return null;
    return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken };
  } catch {
    return null;
  }
}

function writeTokens(tokens: AuthTokens | null): void {
  if (tokens) localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(TOKEN_STORAGE_KEY);
}

type Listener = (tokens: AuthTokens | null) => void;
const listeners = new Set<Listener>();

export function onAuthChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setTokens(tokens: AuthTokens | null): void {
  writeTokens(tokens);
  for (const listener of listeners) listener(tokens);
}

export function getTokens(): AuthTokens | null {
  return readTokens();
}

export function isSignedIn(): boolean {
  return readTokens() !== null;
}

let refreshInFlight: Promise<AuthTokens | null> | null = null;

async function refreshTokens(): Promise<AuthTokens | null> {
  const current = readTokens();
  if (!current) return null;

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(apiUrl("/api/v1/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 422) setTokens(null);
        return null;
      }

      const next = (await res.json()) as AuthTokens;
      setTokens(next);
      return next;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function parseError(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as
    | { error?: string; message?: string }
    | null;
  return new ApiError(
    res.status,
    body?.error ?? "UNKNOWN",
    body?.message ?? res.statusText ?? "Request failed",
  );
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, anonymous = false, signal } = options;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;

    return fetch(apiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  const tokens = anonymous ? null : readTokens();
  let res = await send(tokens?.accessToken ?? null);

  if (res.status === 401 && !anonymous && tokens) {
    const refreshed = await refreshTokens();
    if (!refreshed) throw new SessionExpiredError();
    res = await send(refreshed.accessToken);
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
};

export async function requestMagicLink(email: string): Promise<void> {
  await apiRequest<{ message: string }>("/api/v1/auth/request-link", {
    method: "POST",
    body: { email },
    anonymous: true,
  });
}

export interface RegisterInput {
  email: string;
  displayName?: string;
  agreedToTerms: boolean;
  confirmedAge: boolean;
  consentHealthProcessing: boolean;
}

export async function registerUser(input: RegisterInput): Promise<void> {
  await apiRequest<{ message: string }>("/api/v1/auth/register", {
    method: "POST",
    body: input,
    anonymous: true,
  });
}

export async function verifyMagicLink(token: string): Promise<void> {
  const tokens = await apiRequest<AuthTokens>("/api/v1/auth/verify", {
    method: "POST",
    body: { token },
    anonymous: true,
  });
  setTokens(tokens);
}

export async function signOut(): Promise<void> {
  const tokens = readTokens();
  setTokens(null);
  if (!tokens) return;
  await fetch(apiUrl("/api/v1/auth/logout"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  }).catch(() => undefined);
}
